import { CheckCircle2, Upload } from "lucide-react";
import * as React from "react";

import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { Label } from "../label";
import { T } from "./csv-file-parser.i18n";
import { csvLineErrorMessage } from "./csv-parse-error";
import { type CsvDataPoint, parseCsvToTimeSeriesStrict } from "./csv-utils";

/**
 * CsvFileParser - A drop zone for parsing CSV time series data.
 *
 * @description
 * Accepts a CSV by drop, by click-to-browse or by keyboard, parses it, and
 * returns the parsed data via callback. Once a file parses, the zone is
 * replaced by a summary of what was read — the zone has done its job and
 * leaving it there invites a second, accidental upload.
 *
 * @example
 * ```tsx
 * import { CsvFileParser, CsvDataPoint } from "@/components/ui/csv-parser";
 *
 * const [parsedData, setParsedData] = useState<CsvDataPoint[] | null>(null);
 *
 * <CsvFileParser
 *   id="csv-upload"
 *   label="Upload CSV File"
 *   onParsed={(data) => setParsedData(data)}
 *   onError={(error) => console.error(error)}
 * />
 * ```
 *
 * @example Expected CSV format:
 * ```
 * Date;Value
 * 2026-01-01;100.5
 * 2026-01-02;101.2
 * 2026-01-03;99.8
 * ```
 */
type CsvFileParserProps = {
  /** HTML id attribute for the file input element */
  id?: string;
  /** Label text displayed above the drop zone */
  label?: string;
  /** Callback fired when CSV is successfully parsed */
  onParsed: (data: CsvDataPoint[]) => void;
  /** Callback fired when parsing fails */
  onError?: (error: string) => void;
  /** Shows a required indicator (*) next to the label */
  required?: boolean;
  /** Custom error message shown when CSV format is invalid */
  errorMessage?: string;
  /** Optional helper text describing the required file structure */
  helpText?: string;
  /** Accept attribute for file input (default: ".csv,text/csv") */
  accept?: string;
  /** Call to action inside the drop zone */
  buttonText?: string;
  /** Second line inside the drop zone */
  hintText?: string;
  /** Rejects anything larger, before the file is read (default: 5) */
  maxSizeMb?: number;
  /** Disables the drop zone */
  disabled?: boolean;
};

/**
 * CsvFileParser Component
 *
 * @see {@link CsvFileParserProps} for available props
 */
function CsvFileParser({
  id,
  label,
  onParsed,
  onError,
  required,
  errorMessage,
  helpText,
  accept = ".csv,text/csv",
  buttonText,
  hintText,
  maxSizeMb = 5,
  disabled,
}: CsvFileParserProps) {
  const t = useT(T);
  const { userLanguage } = useUserLanguage();
  const [error, setError] = React.useState<string | null>(null);
  const [summary, setSummary] = React.useState<{ name: string; rows: number } | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const hint = hintText ?? t.sizeHint(maxSizeMb);

  const fail = (message: string) => {
    setSummary(null);
    setError(message);
    onError?.(message);
  };

  const handleFile = async (file: File) => {
    setIsLoading(true);
    setError(null);

    // Validated here rather than after the read: a rejected extension should
    // never cost the time of loading the file into memory.
    if (!/\.csv$/i.test(file.name)) {
      setIsLoading(false);
      fail(t.onlyCsv);
      return;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      setIsLoading(false);
      fail(t.tooLarge(maxSizeMb));
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCsvToTimeSeriesStrict(text, { headerMode: "required" });

      if (parsed.success) {
        setError(null);
        setSummary({ name: file.name, rows: parsed.data.length });
        onParsed(parsed.data);
      } else {
        fail(
          `${errorMessage ?? t.fileFormatError}\n${csvLineErrorMessage(parsed.error, parsed.lineNumber, userLanguage)}`,
        );
      }
    } catch {
      fail(t.readFailed);
    } finally {
      setIsLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openPicker = () => {
    if (!disabled && !isLoading) inputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id}>
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
        className="hidden"
      />

      {summary ? (
        <div className="flex h-9 items-center gap-2 rounded-md border bg-card px-3 text-sm shadow-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
          <span className="min-w-0 flex-1 truncate">
            {summary.name} · {t.rows(summary.rows)}
          </span>
          <button
            type="button"
            onClick={() => {
              setSummary(null);
              openPicker();
            }}
            className="shrink-0 text-sm font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {t.replace}
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={openPicker}
          onDragOver={(e) => {
            e.preventDefault();
            if (disabled) return;
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file && !disabled) void handleFile(file);
          }}
          className={cn(
            "flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed p-4 text-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            isDragging
              ? "border-2 border-primary bg-primary/5"
              : error
                ? "border-destructive bg-destructive/5"
                : "border-muted-foreground bg-background hover:bg-accent/40",
          )}
        >
          <Upload className="h-[18px] w-[18px] text-muted-foreground" />
          <span className="text-sm">
            {isLoading ? t.parsing : isDragging ? t.releaseToUpload : (buttonText ?? t.browse)}
          </span>
          <span className="text-[10px] text-muted-foreground">{hint}</span>
        </button>
      )}

      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      {error && <p className="whitespace-pre-line text-sm text-destructive">{error}</p>}
    </div>
  );
}

export { CsvFileParser };
